/**
 * 사용자 관리 API (계정 영역) — 근태 › 사용자 관리 (2026-08-25 신규).
 *
 * 관리 콘솔(8444)의 `admin/frontend/src/api/{users,adminConsole,institution}.ts` 중
 * **사용자 관리 화면이 쓰는 부분만** 그대로 옮겨 온 것이다. 함수 이름·인자·반환 모양을 원본과
 * 같게 두어 두 화면이 어긋나지 않게 한다(같은 서버 라우트를 부른다 — 서버 변경 0).
 *
 * 경로가 같은 이유는 `admin-org.ts` 머리말과 같다 — 콘솔은 BFF `/api` + `/admin/...`,
 * 8443 은 baseURL `/api/v1` + 같은 상대 경로라 **같은 chat-server 라우트**에 닿는다.
 *
 * ⚠ 이 모듈의 이름들은 기존 `api/adminConsole.ts` 와 일부 겹친다(changeUserRole/createUser).
 *   기존 파일은 한 줄도 건드리지 않았다 — 그쪽은 `AssignableRole` 이 STAFF|MANAGER 로 좁고
 *   이 화면은 관리자·시스템관리자까지 다뤄야 해서 별도 모듈로 둔다. 무엇을 실제로 고를 수
 *   있는지는 로그인한 사람의 등급이 정하고(`lib/role-rank.ts`), 최종 판정은 서버가 한다.
 */
import { apiClient } from './client';

/* ── 목록 ─────────────────────────────────────────────────────────────── */

export interface UserListItem {
  id: string;
  institutionId: string;
  email: string;
  name: string;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  role: string;   // SYSTEM_ADMIN | ADMIN | MANAGER | STAFF
  /**
   * ⚠⚠ **이 칸은 «계정 상태»가 아니라 «접속 상태»다** (2026-08-31 운영 실측).
   *    `/users` 는 `online | away | busy | offline` 을 준다 — 운영 11명이 전부 `offline` 이었다.
   *    계정이 살아 있는지는 **`isActive`** 가 답한다. 계정 상태(active/invited/disabled)는
   *    상세(`GET /admin/users/:id`)에만 있다.
   */
  status: string; // online | away | busy | offline  (⚠ 계정 상태 아님)
  /** 계정이 살아 있는가 — 「활성」을 셀 때 쓰는 칸. 옛 서버는 안 줄 수 있어 옵셔널. */
  isActive?: boolean;
  /** 목록 응답(USER_PUBLIC_SELECT)에는 없다 — 상세(GET /admin/users/:id)에서만 채워진다. */
  employeeNumber?: string | null;
}

export interface UserListResult { users: UserListItem[]; nextCursor: string | null }

export async function listUsers(params?: { cursor?: string; limit?: number }): Promise<UserListResult> {
  const { data } = await apiClient.get<UserListResult>('/users', {
    params: { limit: params?.limit ?? 100, ...(params?.cursor ? { cursor: params.cursor } : {}) },
  });
  // 방어 가드 — users 가 배열이 아닌 비정형 응답에도 소비처 map 이 크래시하지 않도록.
  return { ...data, users: Array.isArray(data?.users) ? data.users : [] };
}

/* ── 권한 변경 ────────────────────────────────────────────────────────── */

export type AssignableRole = 'STAFF' | 'MANAGER' | 'ADMIN' | 'SYSTEM_ADMIN';
export interface UserRoleChangeResponse {
  userId: string;
  email: string;
  role: AssignableRole;
  previousRole: string;
  idempotent?: boolean;
}

export async function changeUserRole(
  userId: string,
  role: AssignableRole,
): Promise<UserRoleChangeResponse> {
  const { data } = await apiClient.patch<UserRoleChangeResponse>(`/admin/users/${userId}/role`, { role });
  return data;
}

/* ── 신규 등록 ────────────────────────────────────────────────────────── */

export type PasswordMode = 'invite_link' | 'temp_password';
export interface CreateUserPayload {
  email: string;
  name: string;
  role?: AssignableRole;
  position?: string;
  phone?: string;
  passwordMode?: PasswordMode;
  /** 사번(사번 로그인용). temp_password 모드에서는 서버가 필수로 본다. */
  employeeNumber?: string;
}
export interface CreateUserResponse {
  user: { id: string; email: string; name: string; role: string; position: string | null; phone: string | null };
  invite: { kind: PasswordMode; token?: string; tempPassword?: string; expiresAt: string };
}

export async function createUser(payload: CreateUserPayload): Promise<CreateUserResponse> {
  const { data } = await apiClient.post<CreateUserResponse>('/admin/users', payload);
  return data;
}

/* ── 상세 · 정보 수정 · 비밀번호 ───────────────────────────────────────── */

export interface AdminUserDetail {
  id: string;
  institutionId: string | null;
  email: string;
  name: string;
  employeeNumber: string | null;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  role: string;
  status: string;
  isActive: boolean;
  /** 비밀번호 설정 여부(해시 자체는 서버가 절대 노출 안 함). false = 초대/임시비번 대기 상태. */
  hasPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminUserProfilePatch {
  name?: string;
  email?: string;
  employeeNumber?: string | null;
  position?: string | null;
  phone?: string | null;
  isActive?: boolean;
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<{ user: AdminUserDetail }>(`/admin/users/${userId}`);
  return data.user;
}

export async function updateAdminUserProfile(
  userId: string,
  patch: AdminUserProfilePatch,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.patch<{ user: AdminUserDetail }>(`/admin/users/${userId}`, patch);
  return data.user;
}

/** 비밀번호 직접 지정 — 즉시 로그인 가능 상태로 전환 + 기존 세션 전량 만료. */
export async function setAdminUserPassword(
  userId: string,
  newPassword: string,
): Promise<{ ok: boolean; userId: string; email: string }> {
  const { data } = await apiClient.post<{ ok: boolean; userId: string; email: string }>(
    `/admin/users/${userId}/password`, { newPassword },
  );
  return data;
}

export interface AdminPasswordResetResult {
  userId: string;
  email: string;
  employeeNumber: string | null;
  invite: { kind: PasswordMode; token?: string; tempPassword?: string; expiresAt: string };
}

/** 비밀번호 초기화 — 기존 비번 폐기 후 임시비번(사번 필요) 또는 초대링크 재발급. */
export async function resetAdminUserPassword(
  userId: string,
  mode: PasswordMode,
): Promise<AdminPasswordResetResult> {
  const { data } = await apiClient.post<AdminPasswordResetResult>(
    `/admin/users/${userId}/password/reset`, { mode },
  );
  return data;
}

/* ── 회사 이름 ────────────────────────────────────────────────────────── */

/**
 * 조직 트리의 루트 라벨(회사명)에만 쓴다.
 *
 * 콘솔은 BFF `/api/admin/institution` 을 부르지만 그 BFF 는 chat-server `/api/v1/institution`
 * 으로 넘길 뿐이다 — 8443 에서는 그 원본 라우트를 직접 부른다(로그인만 하면 읽을 수 있다).
 */
export interface InstitutionBrief {
  id: string;
  code: string;
  name: string;
  domain: string | null;
}

export async function getInstitution(): Promise<InstitutionBrief> {
  const { data } = await apiClient.get<{ institution: InstitutionBrief }>('/institution');
  return data.institution;
}
